import { useNavigate } from '@tanstack/react-router';
import { Button } from '@components/ui';
import { useTranslation } from '@hooks/useTranslation';
import styles from './ReviewerHeader.module.scss';

interface ReviewerHeaderProps {
  title: string;
  reviewerName: string;
  backTo: string;
  /** Author marked their side complete — non-blocking info banner. */
  authorDone?: boolean;
  submitPending: boolean;
  completePending: boolean;
  anyPending: boolean;
  onSubmit: () => void;
  onComplete: () => void;
}

/**
 * Pure presentational header for the reviewer UI. Both action buttons stay
 * mounted in the DOM during any mutation — each shows its own spinner via the
 * `loading` prop, and both are disabled while either is in flight. This is
 * the same pattern as ds-external/ReviewPage; keeping the buttons visible
 * avoids the "buttons disappeared" flash during fast API responses.
 */
export function ReviewerHeader({
  title,
  reviewerName,
  backTo,
  authorDone = false,
  submitPending,
  completePending,
  anyPending,
  onSubmit,
  onComplete,
}: ReviewerHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate({ to: backTo })}
          >
            {'\u2190'} {t('K_BACK', 'Back')}
          </button>
          <div className={styles.titleBlock}>
            <span className={styles.title} title={title}>{title}</span>
            <span className={styles.subtitle}>
              {t('K_REVIEWER_REVIEWING_AS', 'Reviewing as {name}').replace('{name}', reviewerName)}
            </span>
          </div>
          <span className={`${styles.badge} ${styles.badgeInfo}`}>
            {t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review')}
          </span>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="outline"
            size="sm"
            loading={submitPending}
            disabled={anyPending}
            onClick={onSubmit}
          >
            {t('K_REVIEWER_SUBMIT_CHANGES', 'Submit Changes')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            loading={completePending}
            disabled={anyPending}
            onClick={onComplete}
          >
            {t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete')}
          </Button>
        </div>
      </div>
      {authorDone && (
        <div className={styles.authorDoneBanner}>
          {t(
            'K_REVIEWER_AUTHOR_DONE_BANNER',
            'The author has marked their side complete. Once you mark complete, the review will close.',
          )}
        </div>
      )}
    </>
  );
}
